'use client';
import React, { useState, useMemo } from 'react';
import { LESSONS } from '@/lib/mockData';
import LessonCard from '@/components/ui/LessonCard';
import Icon from '@/components/ui/AppIcon';
import FilterSidebar from './FilterSidebar';
import ActiveFilterChips from './ActiveFilterChips';
import LibraryEmptyState from './LibraryEmptyState';

export interface Filters {
  grades: string[];
  topics: string[];
  sdgs: number[];
  durations: string[];
  freeOnly: boolean;
}

const SORT_OPTIONS = [
  { value: 'featured', label: 'Featured First' },
  { value: 'downloads', label: 'Most Downloaded' },
  { value: 'newest', label: 'Newest First' },
  { value: 'title', label: 'A–Z' },
];

export default function LessonLibraryClient() {
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Filters>({
    grades: [],
    topics: [],
    sdgs: [],
    durations: [],
    freeOnly: false,
  });
  const [sort, setSort] = useState('featured');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [favorites, setFavorites] = useState<Set<string>>(new Set(['lesson-001', 'lesson-004']));

  const toggleFavorite = (id: string) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const filtered = useMemo(() => {
    let result = [...LESSONS];

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (l) =>
          l.title.toLowerCase().includes(q) ||
          l.description.toLowerCase().includes(q) ||
          l.topic.toLowerCase().includes(q)
      );
    }

    if (filters.grades.length > 0) {
      result = result.filter((l) => filters.grades.includes(l.gradeBand));
    }
    if (filters.topics.length > 0) {
      result = result.filter((l) => filters.topics.includes(l.topic));
    }
    if (filters.sdgs.length > 0) {
      result = result.filter((l) => filters.sdgs.some((s) => l.sdgs.includes(s)));
    }
    if (filters.durations.length > 0) {
      result = result.filter((l) => filters.durations.includes(l.duration));
    }
    if (filters.freeOnly) {
      result = result.filter((l) => l.isFree);
    }

    if (sort === 'downloads') result.sort((a, b) => b.downloads - a.downloads);
    else if (sort === 'newest') result.sort((a, b) => (a.isNew === b.isNew ? 0 : a.isNew ? -1 : 1));
    else if (sort === 'title') result.sort((a, b) => a.title.localeCompare(b.title));
    else result.sort((a, b) => (a.isFeatured === b.isFeatured ? 0 : a.isFeatured ? -1 : 1));

    return result;
  }, [search, filters, sort]);

  const activeFilterCount =
    filters.grades.length +
    filters.topics.length +
    filters.sdgs.length +
    filters.durations.length +
    (filters.freeOnly ? 1 : 0);

  const clearFilter = (type: keyof Filters, value?: string | number) => {
    setFilters((prev) => {
      if (type === 'freeOnly') return { ...prev, freeOnly: false };
      if (type === 'grades') return { ...prev, grades: prev.grades.filter((v) => v !== value) };
      if (type === 'topics') return { ...prev, topics: prev.topics.filter((v) => v !== value) };
      if (type === 'sdgs') return { ...prev, sdgs: prev.sdgs.filter((v) => v !== value) };
      if (type === 'durations') return { ...prev, durations: prev.durations.filter((v) => v !== value) };
      return prev;
    });
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Page header */}
      <div className="bg-white border-b border-stone-100">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16 py-8">
          <h1 className="font-display text-3xl font-bold text-stone-800 mb-1">Lesson Library</h1>
          <p className="text-stone-500 text-sm">
            {filtered.length} lessons across all grade bands and topics
          </p>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16 py-8">
        {/* Search + Sort bar */}
        <div className="flex items-center gap-3 mb-5">
          <div className="relative flex-1 max-w-lg">
            <Icon
              name="MagnifyingGlassIcon"
              size={16}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              type="text"
              placeholder="Search lessons, topics, SDGs…"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-stone-200 bg-white text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all duration-150"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 transition-colors"
                aria-label="Clear search"
              >
                <Icon name="XMarkIcon" size={14} />
              </button>
            )}
          </div>

          {/* Mobile filter toggle */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden flex items-center gap-2 px-4 py-2.5 rounded-xl border border-stone-200 bg-white text-sm font-medium text-stone-700 hover:bg-stone-50 transition-all duration-150 relative"
          >
            <Icon name="FunnelIcon" size={16} />
            Filters
            {activeFilterCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-primary-500 text-white text-xs flex items-center justify-center font-bold">
                {activeFilterCount}
              </span>
            )}
          </button>

          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-stone-500 hidden sm:inline">Sort:</span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="px-3 py-2.5 rounded-xl border border-stone-200 bg-white text-sm text-stone-700 focus:outline-none focus:ring-2 focus:ring-primary-300 transition-all duration-150"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={`sort-${opt.value}`} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Active filter chips */}
        {activeFilterCount > 0 && (
          <ActiveFilterChips
            filters={filters}
            onClear={clearFilter}
            onClearAll={() =>
              setFilters({ grades: [], topics: [], sdgs: [], durations: [], freeOnly: false })
            }
          />
        )}

        <div className="flex gap-7">
          {/* Desktop sidebar */}
          <aside className="hidden lg:block w-60 flex-shrink-0">
            <FilterSidebar filters={filters} setFilters={setFilters} />
          </aside>

          {/* Mobile sidebar drawer */}
          {sidebarOpen && (
            <div className="fixed inset-0 z-50 lg:hidden">
              <div
                className="absolute inset-0 bg-black/40"
                onClick={() => setSidebarOpen(false)}
              />
              <div className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-2xl overflow-y-auto animate-slide-up">
                <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100 sticky top-0 bg-white z-10">
                  <span className="font-semibold text-stone-800">Filters</span>
                  <button
                    onClick={() => setSidebarOpen(false)}
                    className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-100 transition-all duration-150"
                    aria-label="Close filters"
                  >
                    <Icon name="XMarkIcon" size={18} />
                  </button>
                </div>
                <div className="p-5">
                  <FilterSidebar filters={filters} setFilters={setFilters} />
                </div>
              </div>
            </div>
          )}

          {/* Lesson grid */}
          <div className="flex-1 min-w-0">
            {filtered.length === 0 ? (
              <LibraryEmptyState
                onClearFilters={() => {
                  setSearch('');
                  setFilters({ grades: [], topics: [], sdgs: [], durations: [], freeOnly: false });
                }}
              />
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5">
                  {filtered.map((lesson) => (
                    <LessonCard
                      key={lesson.id}
                      lesson={lesson}
                      onFavorite={toggleFavorite}
                      isFavorited={favorites.has(lesson.id)}
                    />
                  ))}
                </div>

                {/* Pagination */}
                <div className="mt-10 flex items-center justify-between text-sm text-stone-500">
                  <span>Showing {filtered.length} of {LESSONS.length} lessons</span>
                  <div className="flex items-center gap-1">
                    <button className="px-3 py-2 rounded-lg hover:bg-stone-100 transition-colors disabled:opacity-40" disabled>
                      <Icon name="ChevronLeftIcon" size={16} />
                    </button>
                    {[1, 2, 3].map((p) => (
                      <button
                        key={`page-${p}`}
                        className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                          p === 1
                            ? 'bg-primary-500 text-white' :'hover:bg-stone-100 text-stone-600'
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                    <button className="px-3 py-2 rounded-lg hover:bg-stone-100 transition-colors">
                      <Icon name="ChevronRightIcon" size={16} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}